'use strict';
import Logger from '../global/Logger';
import { querySelectorAllz } from './SxQueryObject';
import SxQueryShared from './SxQueryShared';
const parseHTML = SxQueryShared.parseHTML;
// Cached result of the registrable-domain probe. `undefined` => not computed yet, `null` => host-only (IP/localhost/single-label host).
let registrableDomain;
const getDomainCandidates = () => {
    const { hostname } = window.location;
    // Host-only cookie for IP addresses (IPv4 dotted-quad / IPv6 literal) and single-label hosts such as `localhost` - domain cookies are invalid there.
    const isIpAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.indexOf(':') !== -1;
    if (!hostname || isIpAddress || hostname.indexOf('.') === -1) {
        return [];
    }
    const parts = hostname.split('.');
    const candidates = [];
    for (let i = parts.length - 2; i >= 0; i--) {
        candidates.push(parts.slice(i).join('.'));
    }
    return candidates;
};
const getRegistrableDomain = () => {
    if (registrableDomain !== undefined) {
        return registrableDomain;
    }
    const candidates = getDomainCandidates();
    // Random probe-cookie name so a real cookie (or a second widget instance probing concurrently) can never be mistaken for a successful probe.
    const testName = `ss360-cdt-${Math.random().toString(36).slice(2, 10)}`;
    registrableDomain = null;
    // Start at the broadest candidate and move inward; the first that the browser accepts is the eTLD+1.
    for (let i = 0; i < candidates.length; i++) {
        const candidate = candidates[i];
        document.cookie = `${testName}=1; domain=${candidate}; path=/; SameSite=Strict`;
        if (document.cookie.indexOf(`${testName}=`) !== -1) {
            // It stuck => `candidate` is the registrable domain. Remove the probe cookie again.
            document.cookie = `${testName}=; domain=${candidate}; path=/; max-age=0; SameSite=Strict`;
            registrableDomain = candidate;
            break;
        }
    }
    return registrableDomain;
};
const createCookie = (name, value, days, shareAcrossSubdomains = false) => {
    let expires;
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
    }
    else {
        expires = '';
    }
    const writeCookie = (domain) => {
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/${domain}; SameSite=Strict`;
    };
    if (!shareAcrossSubdomains) {
        writeCookie('');
        return;
    }
    if (days < 0 && registrableDomain === undefined) {
        // Deleting before the registrable domain has been probed (e.g. opt-out): don't probe - it would write
        // cookies as a side effect. Expiring the cookie at every candidate scope is side-effect free instead:
        // the browser rejects public-suffix candidates and the rest expire immediately.
        const candidates = getDomainCandidates();
        if (candidates.length === 0) { // hosts without valid domain cookies (IP/localhost) - the cookie can only be host-only
            writeCookie('');
        }
        candidates.forEach((candidate) => {
            writeCookie(`; domain=${candidate}`);
        });
        return;
    }
    const registrable = getRegistrableDomain();
    writeCookie(registrable !== null ? `; domain=${registrable}` : '');
};
const readCookie = (name) => {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return decodeURIComponent(c.substring(nameEQ.length, c.length));
        }
    }
    return null;
};
const extend = (out, ...args) => {
    const output = out || {};
    for (let i = 0; i < args.length; i++) {
        const input = args[i];
        if (input) {
            Object.keys(input).forEach((key) => {
                if (input[key] !== undefined) {
                    if (isObject(output[key]) && isObject(input[key])) {
                        output[key] = extend(output[key], input[key]);
                    }
                    else {
                        output[key] = input[key];
                    }
                }
            });
        }
    }
    return output;
};
const isObject = (el) => el !== undefined && typeof el === 'object' && !(el instanceof Array);
const each = (data, callback) => {
    if (callback && typeof callback === 'function') {
        if (data instanceof Array) { // array
            data.forEach((item, i) => {
                callback(i, item, i);
            });
        }
        else { // object
            Object.keys(data).forEach((key, idx) => {
                callback(key, data[key], idx);
            });
        }
    }
    return data;
};
var Method;
(function (Method) {
    Method["GET"] = "GET";
    Method["POST"] = "POST";
})(Method || (Method = {}));
const ajax = (dataObject, retry = true, disableAdditionalHeaders = false) => {
    dataObject = dataObject || { url: '' };
    const method = dataObject.method || 'GET';
    const dataType = dataObject.dataType;
    const url = dataObject.url;
    const success = dataObject.success || function () { };
    const error = dataObject.error || function () { };
    const forceXDR = 'XDomainRequest' in window;
    // eslint-disable-next-line no-undef
    // @ts-ignore
    const request = forceXDR ? new XDomainRequest() : new XMLHttpRequest();
    request.open(method, forceXDR ? url.replace('https://', '//') : url, true);
    request.onload = function () {
        if (forceXDR || (request.status >= 200 && request.status < 400)) {
            const response = request.responseText;
            if (request.status === 204 || request.status === 304) {
                success();
            }
            else if (!dataType || dataType === 'json' || dataType === 'application/json') {
                let result = null;
                try {
                    result = JSON.parse(response);
                }
                catch (e) {
                    Logger.warn(e);
                    success(response);
                }
                if (result !== null) {
                    success(result);
                }
            }
        }
        else {
            error(request.status, request.responseText || request.statusText);
            // mmmmm...
        }
    };
    request.onerror = function () {
        if (retry && !disableAdditionalHeaders) {
            ajax(dataObject, false, true);
        }
        else {
            error(request.status, request.statusText);
        }
    };
    const send = function () {
        try {
            if (!disableAdditionalHeaders) { // some browsers do not understand the Access-Control-Allow-Headers: '*' flag
                const versionParts = SS360_VERSION.split('.');
                const major = versionParts.splice(0, 1)[0];
                const minor = versionParts.join('.');
                request.setRequestHeader('SEARCHINTEGRATION', 'JAVASCRIPT');
                request.setRequestHeader('SEARCHINTEGRATIONVER', major);
                request.setRequestHeader('EXTVER', minor);
            }
            const requestHeaders = dataObject.headers || {};
            Object.keys(requestHeaders).forEach((key) => {
                request.setRequestHeader(key, requestHeaders[key]);
            });
            if (method !== 'POST') {
                request.send();
            }
            else {
                let data = '';
                if (dataType === 'application/json') {
                    data = JSON.stringify(dataObject.data || {});
                    request.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
                }
                else {
                    each(dataObject.data, (key, value) => {
                        if (value !== undefined || value !== null) {
                            value = `${value}`;
                            if (value.indexOf('&') !== -1 || value.indexOf('=') !== -1 || value.indexOf('+') !== -1) {
                                value = encodeURIComponent(value);
                            }
                            data += `${key}=${value}&`;
                        }
                    });
                    if (data.length > 0) {
                        data = data.substring(0, data.length - 1);
                    }
                    request.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
                }
                request.send(data);
            }
        }
        catch (e) {
            error(request.status, request.statusText, e);
        }
    };
    if (forceXDR) {
        setTimeout(send, 0);
    }
    else {
        send();
    }
};
const querySelectorAll = querySelectorAllz;
const prefersReducedMotion = SxQueryShared.prefersReducedMotion;
const srOnlyCss = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0';
const requestAnimationFrame = SxQueryShared._requestAnimation;
export default {
    each,
    indexInNodeList(el, arr) {
        for (let i = 0; i < arr.length; i++) {
            if (arr[i] === el) {
                return i;
            }
        }
        return -1;
    },
    createCookie,
    readCookie,
    linkOpensInNewTab(event) {
        return event.ctrlKey // CTRL
            || (event.which === 2 || event.button === 4) // middle mouse click
            || (event.target && event.target.target && event.target.target === '_blank'); // target="_blank"
    },
    matchesMediaQuery(type, breakpoint) {
        if (window.matchMedia) {
            const mQuery = type === 'max' ? `(max-width: ${breakpoint}px)` : `(min-width: ${breakpoint}px)`;
            return window.matchMedia(mQuery).matches;
        }
        const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        if (type === 'max') {
            return w <= breakpoint;
        }
        return w >= breakpoint;
    },
    parseHTML,
    extend,
    isObject,
    ajax,
    get(url, success, error, dataType, includeShopSysHeaders = false, headers = {}) {
        ajax({
            url,
            success,
            error,
            dataType,
            headers
        }, true, !includeShopSysHeaders);
    },
    post(url, data, success, dataType, includeShopSysHeaders = false, error, headers = {}) {
        ajax({
            url,
            success,
            dataType,
            method: Method.POST,
            data,
            error,
            headers
        }, true, !includeShopSysHeaders);
    },
    querySelectorAll,
    prefersReducedMotion,
    srOnlyCss,
    requestAnimationFrame,
    _storeObject(key, value, cookieExpirationDays = 365) {
        try {
            const valueString = JSON.stringify(value);
            if ('localStorage' in window) {
                if (value !== null) {
                    window.localStorage.setItem(key, valueString);
                }
                else {
                    window.localStorage.removeItem(key);
                }
            }
            else {
                createCookie(key, valueString, cookieExpirationDays);
            }
        }
        catch (err) {
            // ccl
        }
    },
    _readObject(key, defaultValue) {
        let valueString;
        if ('localStorage' in window) {
            try {
                valueString = window.localStorage.getItem(key);
            }
            catch (err) {
                return defaultValue; // access denied
            }
        }
        else {
            valueString = readCookie(key);
        }
        if (valueString === null || valueString === undefined) {
            return defaultValue;
        }
        try {
            return JSON.parse(valueString);
        }
        catch (err) {
            return defaultValue;
        }
    },
    saveData() {
        // @ts-ignore
        return ('NetworkInformation' in window) && window.NetworkInformation.saveData === true;
    },
    getConnectionEffectiveType() {
        // @ts-ignore
        if ('connection' in navigator && navigator.connection.effectiveType !== undefined) {
            // @ts-ignore
            return navigator.connection.effectiveType;
        }
        return undefined;
    },
    isElementActive(node) {
        if (document.activeElement === node) {
            return true;
        }
        if (node.getRootNode && node.getRootNode().activeElement === node) {
            return true;
        }
        return false;
    },
    uuid() {
        if ('crypto' in window && 'randomUUID' in window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        function s4() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        }
        return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
    }
};
